import { useCallback, useState } from 'react'
import { useGetMentions } from '../../hooks/useGetMentions'
import { useMarkMentionsResolved } from '../../hooks/useMarkMentionsResolved'
import { MentionListDesktopView } from './MentionListDesktopView'
import { MentionListMobileView } from './MentionListMobileView'

export const MentionsList = ({ isMobile }: { isMobile: boolean }) => {
  const [showResolved, setShowResolved] = useState(false)
  const { mutateAsync: markResolved } = useMarkMentionsResolved()

  const {
    data: unresolvedData,
    isLoading: unresolvedLoading,
    fetchNextPage: fetchNextUnresolved,
    hasNextPage: hasNextUnresolved,
    isFetchingNextPage: isFetchingUnresolved,
  } = useGetMentions({ isResolved: false })
  const unresolvedMentions = unresolvedData?.mentions
  const {
    data: resolvedData,
    isLoading: resolvedLoading,
    fetchNextPage: fetchNextResolved,
    hasNextPage: hasNextResolved,
    isFetchingNextPage: isFetchingResolved,
  } = useGetMentions({ isResolved: true })
  const resolvedMentions = resolvedData?.mentions

  const loading = resolvedLoading || unresolvedLoading
  // Count reflects the mentions actually loaded, since "Resolve all" only acts on the loaded set.
  const unresolvedMentionsCount = unresolvedMentions?.length ?? 0
  const displayedMentions = showResolved ? resolvedMentions : unresolvedMentions
  const hasMore = showResolved ? hasNextResolved : hasNextUnresolved
  const isLoadingMore = showResolved ? isFetchingResolved : isFetchingUnresolved
  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return
    void (showResolved ? fetchNextResolved() : fetchNextUnresolved())
  }, [hasMore, isLoadingMore, showResolved, fetchNextResolved, fetchNextUnresolved])

  const markAllMentionsResolved = useCallback(
    () =>
      unresolvedMentions &&
      markResolved({
        mentions: unresolvedMentions.map((m) => ({ mentionId: m.id, requestId: m.requestId })),
        isResolved: true,
      }),
    [markResolved, unresolvedMentions]
  )

  return isMobile ? (
    <MentionListMobileView
      loading={loading}
      unresolvedMentionsCount={unresolvedMentionsCount}
      setShowResolved={setShowResolved}
      showResolved={showResolved}
      markAllMentionsResolved={markAllMentionsResolved}
      displayedMentions={displayedMentions}
      loadMore={loadMore}
      hasMore={hasMore}
    />
  ) : (
    <MentionListDesktopView
      loading={loading}
      unresolvedMentionsCount={unresolvedMentionsCount}
      setShowResolved={setShowResolved}
      showResolved={showResolved}
      markAllMentionsResolved={markAllMentionsResolved}
      displayedMentions={displayedMentions}
      loadMore={loadMore}
      hasMore={hasMore}
    />
  )
}
